import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch } from 'react-icons/fi';
import ChainIcon from '../ChainIcon';
import {
  getSearchDestination,
  getSearchSuggestion,
  truncateQuery,
} from '../../explorer/searchNavigation';

function UniversalSearchBox({
  className = '',
  inputClassName = 'hero-search',
  placeholder = 'Search transactions, blocks, programs and tokens',
  initialQuery = '',
}) {
  const navigate = useNavigate();
  const wrapRef = useRef(null);
  const [value, setValue] = useState(initialQuery);
  const [open, setOpen] = useState(false);
  const [invalid, setInvalid] = useState(false);

  const suggestion = value.trim() ? getSearchSuggestion(value) : null;

  useEffect(() => {
    setValue(initialQuery);
  }, [initialQuery]);

  useEffect(() => {
    setInvalid(false);
  }, [value]);

  useEffect(() => {
    const onPointerDown = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, []);

  const goToResult = (query) => {
    const trimmed = String(query || '').trim();
    if (!trimmed) {
      return;
    }

    const destination = getSearchDestination(trimmed);
    if (!destination) {
      setInvalid(true);
      setOpen(false);
      return;
    }

    setInvalid(false);
    setOpen(false);
    navigate(destination);
  };

  const submit = (event) => {
    event.preventDefault();
    goToResult(value);
  };

  const showSuggestions = open && suggestion;

  return (
    <div
      ref={wrapRef}
      className={`search-wrap universal-search ${className}`.trim()}
    >
      <form className="universal-search__form" onSubmit={submit}>
        <input
          type="search"
          className={inputClassName}
          placeholder={placeholder}
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          autoComplete="off"
          spellCheck={false}
          aria-label="Blockchain search"
          aria-expanded={showSuggestions}
          aria-controls="search-suggestions"
        />
        <button type="submit" className="search-btn" aria-label="Search">
          <FiSearch className="search-btn__icon" aria-hidden />
        </button>
      </form>

      {showSuggestions ? (
        <ul id="search-suggestions" className="search-suggestions" role="listbox">
          <li role="option" aria-selected="true">
            <button
              type="button"
              className="search-suggestions__item"
              onClick={() => goToResult(suggestion.query)}
            >
              <ChainIcon chain={suggestion.iconChain || suggestion.chain} size="lg" className="search-suggestions__icon" />
              <span className="search-suggestions__body">
                <span className="search-suggestions__label">
                  <span className="search-suggestions__chain-name">{suggestion.chainLabel}</span>
                  {' · '}
                  {suggestion.typeLabel || suggestion.label}
                </span>
                <span className="search-suggestions__query">{truncateQuery(suggestion.query)}</span>
              </span>
              <span className="search-suggestions__action">View details</span>
            </button>
          </li>
        </ul>
      ) : null}

      {invalid ? (
        <p className="universal-search__error" role="alert">
          Could not detect blockchain network. Check the address, hash, or block number.
        </p>
      ) : null}
    </div>
  );
}

export default UniversalSearchBox;
